import React, { Component } from 'react';
import { observer, Provider } from 'mobx-react';
import { Link as ReachLink } from '@reach/router';
import { upperFirst } from 'lodash';

import OrderStore from 'js/stores/pages/AdminDashboardPage/OrderStore';
import { formatCurrency } from 'js/services/Format';
import { adminPanelColor } from 'js/config/style';

import LoadingSpinner from '../../LoadingSpinner';
import DashPanel from '../../DashPanel';
import Header from '../../DashPanel/Header';
import Content from '../../DashPanel/Content';
import DropMenu from '../../DashPanel/DropMenu';
import CountDown from 'js/components/core/CountDown';

import Link from './Link';
import Icon from './Icon';
import Title from './Title';
import Text from './Text';

import Card from './Card';
import Display from './Display';
import Image from './Image';
import Details from './Details';
import Label from './Label';
import Price from './Price';

import List from '../../List';
import Row from '../../List/Row';
import ListItem from '../../List/ListItem';
import ListMessage from '../../List/ListMessage';

import User from '../../Display/User';
import Shipment from '../../Display/Shipment';
import CreditCard from '../../Display/CreditCard';
import Transaction from './Transaction';
import OrderStatus from './OrderStatus';

@observer
export default class Order extends Component {
  order = OrderStore.create({ uid: this.props['*'] });

  constructor(props) {
    super(props);
    this.order.load();
  }

  render() {
    const order = this.order;
    return (
      <DashPanel>
        <Header>
          <DropMenu />
          <Title>
            <Link to="/admin/orders">Orders / Offers</Link>
            {order.title && (
              <>
                <Icon name="chevron-right" />
                <Text>{order.title}</Text>
              </>
            )}
          </Title>
        </Header>

        <Provider model={order}>
          <Content background={adminPanelColor}>
            {this.order.isLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                <Card>
                  <Display>
                    <Image src={order.image} />
                  </Display>

                  <Details>
                    <Label>Last updated</Label>
                    <div>{order.updated_at}</div>

                    <Label>Order ID</Label>
                    <div>{order.uid}</div>

                    {order.item_mode === 'item' ? (
                      <>
                        <Label>Item</Label>
                        <div>
                          <ReachLink to={`/admin/items/item/${order.item_alias}`}>{order.title}</ReachLink>
                          <span> ({upperFirst(order.item_size)})</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <Label>Token</Label>
                        <div>
                          <ReachLink to={`/admin/tokens/token/${order.item_alias}`}>{order.title}</ReachLink>
                          <span> ({upperFirst(order.item_size)})</span>
                        </div>
                      </>
                    )}

                    <Label>Price</Label>
                    <div>
                      <Price>{formatCurrency(order.price)}</Price>
                    </div>

                    <Label>Status</Label>
                    <div>
                      <div>{order.human_status}</div>

                      {order.status === 'offer_requested_by_buyer' && (
                        <CountDown remaining={order.seconds_until_expiration}>
                          {({ status, duration, timeLeft, Remaining }) =>
                            status === 'expired' ? (
                              <>
                                (offer{' '}
                                <Remaining status={status} title={order.expiration_time}>
                                  expired
                                </Remaining>
                                )
                              </>
                            ) : (
                              <>
                                (expires in{' '}
                                <Remaining status={status} title={order.expiration_time}>
                                  {status === 'warn' ? timeLeft : duration.humanize()}
                                </Remaining>
                                )
                              </>
                            )
                          }
                        </CountDown>
                      )}

                      {order.status === 'delivered_to_buyer' && (
                        <CountDown remaining={order.seconds_until_completion}>
                          {({ status, duration, timeLeft, Remaining }) =>
                            status === 'expired' ? (
                              <>
                                (order{' '}
                                <Remaining status={status} title={order.completion_time}>
                                  completed
                                </Remaining>
                                )
                              </>
                            ) : (
                              <>
                                (completes in{' '}
                                <Remaining status={status} title={order.completion_time}>
                                  {status === 'warn' ? timeLeft : duration.humanize()}
                                </Remaining>
                                )
                              </>
                            )
                          }
                        </CountDown>
                      )}

                      {order.status === 'order_return_delivered' && (
                        <CountDown remaining={order.seconds_until_returned}>
                          {({ status, duration, timeLeft, Remaining }) =>
                            status === 'expired' ? (
                              <>
                                (order{' '}
                                <Remaining status={status} title={order.returned_time}>
                                  returned
                                </Remaining>
                                )
                              </>
                            ) : (
                              <>
                                (returned in{' '}
                                <Remaining status={status} title={order.returned_time}>
                                  {status === 'warn' ? timeLeft : duration.humanize()}
                                </Remaining>
                                )
                              </>
                            )
                          }
                        </CountDown>
                      )}
                    </div>
                  </Details>
                </Card>

                <div>
                  <Row cols={2}>
                    <User load={{ uid: order.uid, relation: 'seller' }} title="Seller" />
                    <User load={{ uid: order.uid, relation: 'buyer' }} title="Buyer" />
                  </Row>

                  <Row cols={2}>
                    <CreditCard load={{ uid: order.uid, relation: 'seller' }} title="Seller credit card" />
                    <CreditCard load={{ uid: order.uid, relation: 'buyer' }} title="Buyer credit card" />
                  </Row>

                  <Row cols={2}>
                    <Shipment load={{ uid: order.uid, direction: 'deliver' }} title="Delivery shipment" />
                    <Shipment load={{ uid: order.uid, direction: 'return' }} title="Return shipment" />
                  </Row>

                  <Row cols={2}>
                    <List paginator={order.transactions} title="Transactions" icon="list" controls="more">
                      <div>
                        {order.transactions.transactions.length ? (
                          order.transactions.transactions.map(transaction => (
                            <ListItem key={transaction.id} to={`/admin/transactions/transaction/${transaction.id}`}>
                              <Transaction transaction={transaction} />
                            </ListItem>
                          ))
                        ) : (
                          <ListMessage>No transactions found</ListMessage>
                        )}
                      </div>
                    </List>

                    <List paginator={order.statuses} title="Order status changes" icon="list" controls="more">
                      <div>
                        {order.statuses.statuses.length ? (
                          order.statuses.statuses.map(status => (
                            <ListItem
                              key={status.id}
                              to={status.creator_email ? `/admin/users/user/${status.creator_email}` : null}>
                              <OrderStatus status={status} />
                            </ListItem>
                          ))
                        ) : (
                          <ListMessage>No status updates found</ListMessage>
                        )}
                      </div>
                    </List>
                  </Row>
                </div>
              </>
            )}
          </Content>
        </Provider>
      </DashPanel>
    );
  }
}
